<template>
  <div class="p-6 transition-all duration-300 min-h-screen bg-gray-50 dark:bg-gray-950">
    <!-- Header Section -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
      <div class="space-y-1">
        <h1 class="text-3xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">
          Personal Técnico
        </h1>
        <p class="text-gray-500 dark:text-gray-400 text-sm font-medium">
          Gestiona el equipo de especialistas y sus comisiones
        </p>
      </div>
      
      <button 
        @click="abrirModalCrear" 
        class="btn-primary"
      >
        <Icon icon="mdi:account-plus" class="text-xl" />
        <span class="font-bold">Nuevo Técnico</span>
      </button>
    </div>

    <!-- Analytics & Stats Summary -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 text-white">
      <div class="p-6 rounded-3xl bg-gradient-to-br from-blue-500/90 to-indigo-600/90 shadow-xl border border-white/10 backdrop-blur-sm">
        <div class="flex items-center gap-4">
          <div class="p-3 bg-white/20 rounded-2xl">
            <Icon icon="mdi:account-group" class="text-2xl" />
          </div>
          <div>
            <p class="text-blue-100 text-xs font-semibold uppercase tracking-wider">Total Técnicos</p>
            <p class="text-3xl font-bold">{{ tecnicos.length }}</p>
          </div>
        </div>
      </div>
      <div class="p-6 rounded-3xl bg-gradient-to-br from-emerald-500/90 to-teal-600/90 shadow-xl border border-white/10 backdrop-blur-sm">
        <div class="flex items-center gap-4">
          <div class="p-3 bg-white/20 rounded-2xl">
            <Icon icon="mdi:star-outline" class="text-2xl" />
          </div>
          <div>
            <p class="text-emerald-100 text-xs font-semibold uppercase tracking-wider">Especialidades</p>
            <p class="text-3xl font-bold">{{ especialidadesUnicas }}</p>
          </div>
        </div>
      </div>
      <div class="p-6 rounded-3xl bg-gradient-to-br from-amber-500/90 to-orange-600/90 shadow-xl border border-white/10 backdrop-blur-sm">
        <div class="flex items-center gap-4">
          <div class="p-3 bg-white/20 rounded-2xl">
            <Icon icon="mdi:percent" class="text-2xl" />
          </div>
          <div>
            <p class="text-amber-100 text-xs font-semibold uppercase tracking-wider">Comisión Promedio</p>
            <p class="text-3xl font-bold">{{ comisionPromedio }}%</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters & Search Card -->
    <div class="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800 p-6 mb-8">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
        <div class="md:col-span-2 relative group">
          <label class="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 ml-1">Búsqueda Rápida</label>
          <div class="relative">
            <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl group-focus-within:text-blue-500 transition-colors" />
            <input 
              v-model="filtros.busqueda" 
              type="text" 
              placeholder="Buscar por nombre, apellido o especialidad..." 
              class="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all text-gray-700 dark:text-white placeholder-gray-400" 
            >
          </div>
        </div>
        
        <div>
          <label class="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 ml-1">Ordenar por</label>
          <select v-model="filtros.orden" class="w-full py-3.5 bg-gray-50 dark:bg-gray-800 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all text-gray-700 dark:text-white font-medium cursor-pointer">
            <option value="nombre">Nombre (A-Z)</option>
            <option value="comision">Comisión (Mayor a Menor)</option>
            <option value="reciente">Más Recientes</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Data Display -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      <div v-if="cargando" v-for="n in 8" :key="n" class="bg-white dark:bg-gray-900 rounded-3xl p-6 shadow-xl animate-pulse border border-gray-100 dark:border-gray-800">
        <div class="flex items-center gap-4 mb-4">
          <div class="w-16 h-16 bg-gray-200 dark:bg-gray-800 rounded-2xl"></div>
          <div class="flex-1 space-y-2">
            <div class="h-4 bg-gray-200 dark:bg-gray-800 rounded w-3/4"></div>
            <div class="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/2"></div>
          </div>
        </div>
        <div class="space-y-3">
          <div class="h-3 bg-gray-200 dark:bg-gray-800 rounded"></div>
          <div class="h-3 bg-gray-200 dark:bg-gray-800 rounded w-5/6"></div>
          <div class="flex gap-2 pt-2">
            <div class="h-8 bg-gray-200 dark:bg-gray-800 rounded-xl flex-1"></div>
            <div class="h-8 bg-gray-200 dark:bg-gray-800 rounded-xl w-10"></div>
          </div>
        </div>
      </div>

      <div v-else-if="tecnicosFiltrados.length === 0" class="col-span-full py-20 text-center">
        <div class="flex flex-col items-center gap-4 text-gray-400">
          <Icon icon="mdi:account-off-outline" class="text-7xl opacity-20" />
          <p class="text-xl font-medium">No se encontraron técnicos registrados</p>
          <button @click="filtros.busqueda = ''" class="text-blue-500 underline text-sm">Limpiar búsqueda</button>
        </div>
      </div>

      <div v-else v-for="tecnico in tecnicosFiltrados" :key="tecnico.idTecnico" 
           class="group bg-white dark:bg-gray-900 rounded-3xl p-6 shadow-xl border border-gray-100 dark:border-gray-800 hover:shadow-2xl hover:border-blue-500/30 transition-all duration-300 flex flex-col relative overflow-hidden">
        
        <!-- Background Decoration -->
        <div class="absolute -right-10 -top-10 w-32 h-32 bg-blue-500/5 rounded-full group-hover:scale-150 transition-transform duration-700"></div>

        <div class="flex items-center gap-4 mb-5 relative">
          <div class="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400">
            <Icon icon="mdi:account-wrench" class="text-3xl" />
          </div>
          <div class="flex-1 overflow-hidden">
            <h3 class="font-bold text-gray-800 dark:text-white truncate text-lg">{{ tecnico.nombre }} {{ tecnico.apellido }}</h3>
            <p class="text-xs font-semibold text-blue-500 uppercase tracking-wider">{{ tecnico.especialidad || 'Especialista General' }}</p>
          </div>
        </div>

        <div class="space-y-4 flex-1">
          <p class="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 min-h-[40px]">
            {{ tecnico.descripcion || 'Sin descripción profesional registrada.' }}
          </p>
          
          <div class="grid grid-cols-2 gap-3">
            <div class="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-2xl border border-gray-100 dark:border-gray-800">
              <p class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Comisión</p>
              <p class="text-lg font-black text-emerald-500">{{ tecnico.comisionPorcentaje }}%</p>
            </div>
            <div class="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-2xl border border-gray-100 dark:border-gray-800">
              <p class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Desde</p>
              <p class="text-sm font-bold text-gray-700 dark:text-gray-300 mt-1.5">{{ formatearFecha(tecnico.fechaContratacion) }}</p>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2 mt-6 pt-5 border-t border-gray-100 dark:border-gray-800">
          <button 
            @click="abrirModalEditar(tecnico)"
            class="flex-1 py-2.5 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-bold text-sm hover:bg-blue-600 hover:text-white dark:hover:bg-blue-600 dark:hover:text-white transition-all active:scale-95"
          >
            Editar Perfil
          </button>
          <button 
            @click="confirmarEliminar(tecnico)"
            class="p-2.5 rounded-xl bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 hover:bg-rose-600 hover:text-white transition-all active:scale-95 shadow-sm"
          >
            <Icon icon="mdi:trash-can-outline" class="text-xl" />
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL CREAR/EDITAR -->
    <BaseModal 
      v-model="modalVisible" 
      :title="editando ? 'Actualizar Técnico' : 'Vincular Técnico'"
      :icon="editando ? 'mdi:account-edit' : 'mdi:account-plus'"
      iconType="primary"
    >
      <form @submit.prevent="guardarTecnico" class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div v-if="!editando" class="col-span-full">
            <label class="label-premium">Vincular a Usuario</label>
            <div class="relative">
              <Icon icon="mdi:account-search" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
              <select v-model="form.idUsuario" @change="sincronizarDatosUsuario" required class="input-premium pl-12 cursor-pointer">
                <option :value="0" disabled>Selecciona un usuario...</option>
                <option v-for="user in usuariosDisponibles" :key="user.idUsuario" :value="user.idUsuario">
                  {{ user.nombre }} {{ user.apellido }} (@{{ user.nombreUsuario }})
                </option>
              </select>
            </div>
          </div>

          <div>
            <label class="label-premium">Nombre</label>
            <input v-model="form.nombre" type="text" required class="input-premium" @blur="form.nombre = toTitleCase(form.nombre)">
          </div>

          <div>
            <label class="label-premium">Apellido</label>
            <input v-model="form.apellido" type="text" required class="input-premium" @blur="form.apellido = toTitleCase(form.apellido)">
          </div>

          <div>
            <label class="label-premium">Especialidad</label>
            <input v-model="form.especialidad" type="text" placeholder="Ej: Electrónica, Software..." class="input-premium" @blur="form.especialidad = toTitleCase(form.especialidad)">
          </div>

          <div>
            <label class="label-premium">Comisión (%)</label>
            <div class="relative">
              <Icon icon="mdi:percent" class="absolute right-4 top-1/2 -translate-y-1/2 text-emerald-500 text-lg" />
              <input v-model.number="form.comisionPorcentaje" type="number" min="0" max="100" required class="input-premium pr-10">
            </div>
          </div>

          <div class="col-span-full">
            <label class="label-premium">Descripción Profesional</label>
            <textarea v-model="form.descripcion" rows="3" class="input-premium resize-none" @blur="form.descripcion = toTitleCase(form.descripcion)"></textarea>
          </div>

          <div>
            <label class="label-premium">Fecha Contratación</label>
            <input v-model="form.fechaContratacion" type="date" required class="input-premium">
          </div>

          <div>
            <label class="label-premium">Estado</label>
            <div class="flex p-1 bg-gray-100 dark:bg-gray-700/50 rounded-xl">
              <button type="button" @click="form.estado = 'activo'" :class="['flex-1 py-1.5 text-xs font-bold uppercase rounded-lg transition-all', form.estado === 'activo' ? 'bg-white dark:bg-gray-800 text-emerald-500 shadow-sm' : 'text-gray-400']">Activo</button>
              <button type="button" @click="form.estado = 'inactivo'" :class="['flex-1 py-1.5 text-xs font-bold uppercase rounded-lg transition-all', form.estado === 'inactivo' ? 'bg-white dark:bg-gray-800 text-rose-500 shadow-sm' : 'text-gray-400']">Inactivo</button>
            </div>
          </div>
        </div>
        
        <div class="flex gap-4 pt-4">
          <button type="button" @click="cerrarModal" class="flex-1 btn-secondary">Cancelar</button>
          <button type="submit" :disabled="guardando" class="flex-1 btn-primary">
            <Icon :icon="guardando ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': guardando}" class="text-xl" />
            {{ guardando ? 'Guardando...' : 'Guardar' }}
          </button>
        </div>
      </form>
    </BaseModal>

    <!-- MODAL DE CONFIRMACIÓN -->
    <BaseModal 
      v-model="confirmarEliminarVisible" 
      title="¿Eliminar Técnico?"
      icon="mdi:alert-circle"
      iconType="danger"
      maxWidth="max-w-md"
    >
      <div class="text-center py-2">
        <p class="text-gray-600 dark:text-gray-400">
          ¿Estás seguro que deseas eliminar a <strong>{{ tecnicoAEliminar?.nombre }} {{ tecnicoAEliminar?.apellido }}</strong>?
        </p>
        <p class="text-xs text-rose-500 mt-2 font-medium">Esta acción no se puede deshacer.</p>
      </div>
      <template #footer>
        <div class="flex gap-3">
          <button @click="confirmarEliminarVisible = false" class="flex-1 btn-secondary">Cancelar</button>
          <button @click="ejecutarEliminar" class="flex-1 btn-danger">Eliminar</button>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue';
import { useToast } from 'vue-toastification';
import { toTitleCase } from '@/utils/stringUtils.js';
import { notificarTecnico } from '@/utils/notificacionesHelper';
import BaseModal from '@/components/common/BaseModal.vue';
import { obtenerUsuarios } from '@/api/usuarios.js';
import { 
  obtenerTecnicos, crearTecnico, actualizarTecnico, eliminarTecnico, 
  formatearFechaContratacion 
} from '@/api/tecnicos.js';

export default {
  name: 'TecnicosList',
  components: { Icon, BaseModal },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      tecnicos: [],
      usuarios: [],
      cargando: false,
      guardando: false,
      modalVisible: false,
      editando: false,
      filtros: {
        busqueda: '',
        orden: 'nombre'
      },
      form: {
        idTecnico: null,
        idUsuario: 0,
        nombre: '',
        apellido: '',
        descripcion: '',
        especialidad: '',
        comisionPorcentaje: 10,
        fechaContratacion: new Date().toISOString().split('T')[0],
        estado: 'activo'
      },
      confirmarEliminarVisible: false,
      tecnicoAEliminar: null
    };
  },
  computed: {
    tecnicosFiltrados() {
      let result = [...this.tecnicos];
      if (this.filtros.busqueda) {
        const q = this.filtros.busqueda.toLowerCase();
        result = result.filter(t => 
          t.nombre.toLowerCase().includes(q) || 
          t.apellido.toLowerCase().includes(q) || 
          (t.especialidad && t.especialidad.toLowerCase().includes(q))
        );
      }
      if (this.filtros.orden === 'nombre') {
        result.sort((a, b) => a.nombre.localeCompare(b.nombre));
      } else if (this.filtros.orden === 'comision') {
        result.sort((a, b) => b.comisionPorcentaje - a.comisionPorcentaje);
      } else if (this.filtros.orden === 'reciente') {
        result.sort((a, b) => new Date(b.fechaContratacion) - new Date(a.fechaContratacion));
      }
      return result;
    },
    usuariosDisponibles() {
      // Ahora mostramos TODOS los usuarios que no están asignados como técnicos aún
      const idsTecnicosActuales = this.tecnicos.map(t => t.idUsuario);
      return this.usuarios.filter(u => !idsTecnicosActuales.includes(u.idUsuario));
    },
    especialidadesUnicas() {
      const esp = this.tecnicos.map(t => t.especialidad).filter(e => e);
      return new Set(esp).size;
    },
    comisionPromedio() {
      if (this.tecnicos.length === 0) return 0;
      const total = this.tecnicos.reduce((sum, t) => sum + (t.comisionPorcentaje || 0), 0);
      return Math.round(total / this.tecnicos.length);
    }
  },
  mounted() {
    this.cargarDatos();
  },
  methods: {
    toTitleCase,
    formatearFecha: formatearFechaContratacion,

    async cargarDatos() {
      this.cargando = true;
      try {
        const [resTecnicos, resUsuarios] = await Promise.all([
          obtenerTecnicos(),
          obtenerUsuarios('all')
        ]);
        if (resTecnicos.success) this.tecnicos = resTecnicos.data;
        if (resUsuarios.success) this.usuarios = resUsuarios.data;
      } catch (error) {
        this.toast.error('Error al cargar la información del personal');
      } finally {
        this.cargando = false;
      }
    },

    sincronizarDatosUsuario() {
      const usuario = this.usuarios.find(u => u.idUsuario === this.form.idUsuario);
      if (usuario) {
        this.form.nombre = usuario.nombre;
        this.form.apellido = usuario.apellido;
        this.form.especialidad = usuario.especialidad || '';
        this.form.comisionPorcentaje = usuario.comision_porcentaje || 10;
      }
    },

    abrirModalCrear() {
      this.editando = false;
      this.form = {
        idTecnico: null,
        idUsuario: 0,
        nombre: '',
        apellido: '',
        descripcion: '',
        especialidad: '',
        comisionPorcentaje: 10,
        fechaContratacion: new Date().toISOString().split('T')[0],
        estado: 'activo'
      };
      this.modalVisible = true;
    },

    abrirModalEditar(tecnico) {
      this.editando = true;
      this.form = { ...tecnico };
      // Formatear fecha para el input date
      if (this.form.fechaContratacion) {
        this.form.fechaContratacion = new Date(this.form.fechaContratacion).toISOString().split('T')[0];
      }
      this.modalVisible = true;
    },

    cerrarModal() {
      this.modalVisible = false;
    },

    async guardarTecnico() {
      if (!this.editando && (!this.form.idUsuario || this.form.idUsuario === 0)) {
        this.toast.warning('Debes seleccionar un usuario base');
        return;
      }
      
      this.guardando = true;
      try {
        const action = this.editando ? actualizarTecnico(this.form.idTecnico, this.form) : crearTecnico(this.form);
        const res = await action;
        if (res.success) {
          this.toast.success(this.editando ? 'Perfil actualizado' : 'Técnico registrado correctamente');
          notificarTecnico(
            this.editando ? 'Técnico actualizado' : 'Nuevo técnico registrado',
            `${this.form.nombre} ${this.form.apellido}`,
            `Especialidad: ${this.form.especialidad || 'General'} · Comisión: ${this.form.comisionPorcentaje}%`
          );
          this.cargarDatos();
          this.cerrarModal();
        } else {
          this.toast.error(res.error || 'No se pudo completar la operación');
        }
      } catch (error) {
        this.toast.error('Error de comunicación con el servidor');
      } finally {
        this.guardando = false;
      }
    },

    async confirmarEliminar(tecnico) {
      this.tecnicoAEliminar = tecnico;
      this.confirmarEliminarVisible = true;
    },

    async ejecutarEliminar() {
      if (!this.tecnicoAEliminar) return;
      try {
        const result = await eliminarTecnico(this.tecnicoAEliminar.idTecnico);
        if (result.success) {
          this.toast.success('Perfil técnico eliminado');
          notificarTecnico('Técnico eliminado', `${this.tecnicoAEliminar?.nombre} ${this.tecnicoAEliminar?.apellido}`, 'Perfil eliminado del sistema');
          this.cargarDatos();
        } else {
          this.toast.error(result.error || 'Error al eliminar');
        }
      } catch (error) {
        this.toast.error('Error al procesar la solicitud');
      } finally {
        this.confirmarEliminarVisible = false;
        this.tecnicoAEliminar = null;
      }
    }
  }
};
</script>

<style scoped>
.modal-bounce-enter-active { animation: bounce-in 0.4s; }
.modal-bounce-leave-active { animation: bounce-in 0.3s reverse; }
@keyframes bounce-in {
  0% { transform: scale(0.95); opacity: 0; }
  60% { transform: scale(1.02); }
  100% { transform: scale(1); opacity: 1; }
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;  
  line-clamp: 2;
  overflow: hidden;
}
</style>
